import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

export type CouponState   = 'added' | 'applied' | 'expired';
export interface PromotionState {
  promoCode:  string;
  couponCode: string;
  state:      CouponState;
  endsAt?:    number;
}

@Injectable({ providedIn: 'root' })
export class PromoService {
  private readonly STORAGE_KEY = 'promotions';
  private store$ = new BehaviorSubject<PromotionState[]>(this.load());

  list$: Observable<PromotionState[]> = this.store$.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.refreshExpiry();
  }

  addIfNew(promoCode: string, couponCode: string, endsAt?: any): boolean {
    if (this.store$.value.some(p => p.couponCode === couponCode && p.state !== 'expired')) return false;
    this.next([
      ...this.store$.value,
      { promoCode, couponCode, state: 'added', endsAt: new Date(endsAt)?.getTime() || undefined } as PromotionState,
    ]);
    return true;
  }

  markApplied(couponCode: string) { this.updateState(couponCode, 'applied'); }

  markAdded  (couponCode: string) { this.updateState(couponCode, 'added');   }

  resetAllState() {
    const next = this.store$.value.map(p => ({
      ...p,
      state: p.state === 'applied' ? 'added' : p.state,
    }) as PromotionState);
    this.next(next);
  }

  clearAllStates(): void {
    this.store$.next([]);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  get active(): PromotionState[] {
    this.refreshExpiry();
    return this.store$.value.filter(p => p.state === 'added');
  }

  private updateState(code: string, state: CouponState) {
    const next = this.store$.value.map(p =>
      p.couponCode === code ? { ...p, state } : p);
    this.next(next);
  }

  private next(val: PromotionState[]) {
    this.store$.next(val);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(val));
    }
  }

  private load(): PromotionState[] {
    if (isPlatformBrowser(this.platformId)) {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (raw) { try { return JSON.parse(raw); } catch {} }
    }
    return [];
  }

  private isExpired(p: PromotionState, now = new Date()) {
    return !!p.endsAt && new Date(p.endsAt).getTime() < now.getTime();
  }

  private refreshExpiry() {
    const alive = this.store$.value.filter(p => !this.isExpired(p));
    if (alive.length !== this.store$.value.length) this.next(alive);
  }
}
